import { useAppSelector } from '../../store/hooks';
import { selectMemberInformation } from '../../store/slices/onboardingSlice';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { ServiceNationSolutionOrgName } from '../constants';
import { SubscriptionTier } from '../models/Subscription';
import { tiersAboveContentMembership } from '../utils/MembershipTierAccess';

/**
 * Every place in the app that currently re-derives its own subscription/org
 * check for a specific page, nav link or feature. Add new gated
 * pages/links/sections here rather than inlining another copy of the check,
 * and add its rule to ACCESS_RULES below.
 *
 * Scope is deliberately subscription/org-based access control only. Feature-flag
 * rollout status is a separate, orthogonal concern: where a resource is also
 * gated by an Optimizely flag (e.g. Wallet, the Events/Design Services/On-Demand
 * Training links), combine this hook's result with your own `useOptimizelyFlag`
 * call rather than looking for the flag in here.
 *
 * Assumes member/company data is already loaded - DashboardHome blocks
 * rendering its child routes until the initial getMemberInformation fetch
 * resolves, so anything rendered under it (which is every dashboard page)
 * can call this hook without a loading state to handle.
 */
// eslint-disable-next-line no-shadow
export enum AccessResource {
  // Pages
  HomePage = 'homePage',
  SNHomePage = 'SNHomePage',
  ResourcesPage = 'resourcesPage',
  WalletPage = 'walletPage',
  CommunityPage = 'communityPage',
  EventsPage = 'eventsPage',
  DesignServicesPage = 'designServicesPage',
  OnDemandTrainingPage = 'onDemandTrainingPage',
  AdvisoryBoardsPage = 'advisoryBoardsPage',
  AccountPage = 'accountPage',
  MembershipsPage = 'membershipsPage',
  AccountSettingsPage = 'accountSettingsPage',
  FAQPage = 'faqPage',
  // Features
  ManagePayoutMethods = 'managePayoutMethods',
  HistoricalRebate = 'historicalRebate',
  ReportPurchases = 'reportPurchases',
  PartnerRebateMatrix = 'partnerRebateMatrix',
  Logout = 'logout',
}

/**
 * `Edge` = the default (non-Service-Nation) Edge platform. Distinct from
 * `SubscriptionTier.EDGE`, which is the free subscription tier - a member on
 * either org can be on the EDGE tier.
 */
// eslint-disable-next-line no-shadow
enum MemberOrgCategory {
  Edge = 'edge',
  ServiceNation = 'service_nation',
}

interface AccessRule {
  allowedOrgs: 'all' | MemberOrgCategory[];
  allowedEdgeTiers: 'all' | SubscriptionTier[];
  allowedSNTiers: 'all' | SubscriptionTier[];
}

interface useHasAccessOptions {
  serviceNationWalletFlag?: boolean;
  useEdgeHomeForSNFlag?: boolean;
  snSettingsLinkFlag?: boolean;
}

type AccessRuleResolver = (ctx?: useHasAccessOptions) => AccessRule;

/**
 * Tiers above Roundtable's content membership, ie. Advanced and up. Derived from
 * the shared tier maps (`membershipTierMaps` in `@optimus/utils`) so adding a tier
 * no longer means remembering to list it here.
 *
 * Named for content on purpose: a content membership is not the same thing as
 * paying. Association pays yet reads Foundation content, so `isPaidTier` includes
 * it and a content-derived list like this one need not. Use `isPaidTier` for
 * billing questions; use these for entitlement to what a membership unlocks.
 */
const SN_NOT_USE_EDGE_HOME_TIERS = tiersAboveContentMembership('Performance');

/** Every tier except the free Edge tier. Per product's "Membership access by Tier" page. */
const EVERY_TIER_BUT_FREE = Object.values(SubscriptionTier)
  .filter((tier) => tier !== SubscriptionTier.EDGE);

/**
 * Every tier except Edge and Association. Per product's "Membership access by Tier" page.
 * Built from the enum rather than the content map because Association shares
 * Foundation content with Edge, so no content level separates the two.
 */
const EVERY_TIER_BUT_EDGE_AND_ASSOCIATION = Object.values(SubscriptionTier)
  .filter((tier) => tier !== SubscriptionTier.AM && tier !== SubscriptionTier.EDGE);

/**
 * Roundtable and below (Edge, Association, Roundtable), ie. every tier that can
 * still upgrade. Per product's "Membership access by Tier" page.
 */
const ROUNDTABLE_AND_BELOW_TIERS = Object.values(SubscriptionTier)
  .filter((tier) => !SN_NOT_USE_EDGE_HOME_TIERS.includes(tier));

/** The access matrix. One row per `AccessResource`. */
const ACCESS_RULES: Record<AccessResource, AccessRule | AccessRuleResolver> = {
  [AccessResource.HomePage]: (options) => ({
    allowedOrgs: options?.useEdgeHomeForSNFlag ? 'all' : [MemberOrgCategory.Edge],
    allowedEdgeTiers: 'all',
    allowedSNTiers: [SubscriptionTier.AM, SubscriptionTier.SRT],
  }),
  [AccessResource.SNHomePage]: (options) => ({
    allowedOrgs: [MemberOrgCategory.ServiceNation],
    allowedEdgeTiers: [],
    allowedSNTiers: options?.useEdgeHomeForSNFlag ? SN_NOT_USE_EDGE_HOME_TIERS : 'all',
  }),
  [AccessResource.ResourcesPage]: (options) => ({
    allowedOrgs: [MemberOrgCategory.ServiceNation],
    allowedEdgeTiers: [],
    allowedSNTiers: options?.useEdgeHomeForSNFlag ? SN_NOT_USE_EDGE_HOME_TIERS : 'all',
  }),
  [AccessResource.WalletPage]: (options) => ({
    allowedOrgs: options?.serviceNationWalletFlag ? 'all' : [MemberOrgCategory.Edge],
    allowedEdgeTiers: 'all',
    allowedSNTiers: options?.serviceNationWalletFlag ? 'all' : [],
  }),
  [AccessResource.CommunityPage]: (options) => ({
    allowedOrgs: options?.useEdgeHomeForSNFlag ? 'all' : [MemberOrgCategory.Edge],
    allowedEdgeTiers: 'all',
    // Community is for every tier
    allowedSNTiers: options?.useEdgeHomeForSNFlag ? 'all' : [],
  }),
  [AccessResource.EventsPage]: (options) => ({
    allowedOrgs: options?.useEdgeHomeForSNFlag ? 'all' : [MemberOrgCategory.Edge],
    // Events (Training) is for every tier except the free Edge tier, on both orgs
    allowedEdgeTiers: EVERY_TIER_BUT_FREE,
    allowedSNTiers: options?.useEdgeHomeForSNFlag ? EVERY_TIER_BUT_FREE : [],
  }),
  [AccessResource.DesignServicesPage]: (options) => ({
    allowedOrgs: options?.useEdgeHomeForSNFlag ? 'all' : [MemberOrgCategory.Edge],
    // Design Services is for every paid tier, but not the free Edge tier
    allowedEdgeTiers: EVERY_TIER_BUT_FREE,
    allowedSNTiers: options?.useEdgeHomeForSNFlag ? EVERY_TIER_BUT_FREE : [],
  }),
  [AccessResource.AdvisoryBoardsPage]: {
    // Advisory Boards is only for Service Nation Alliance, Advanced, Coalition and Pinnacle members
    allowedOrgs: [MemberOrgCategory.ServiceNation],
    allowedEdgeTiers: [],
    allowedSNTiers: [
      SubscriptionTier.ADV,
      SubscriptionTier.ADVP,
      SubscriptionTier.CC,
      SubscriptionTier.PIN,
      SubscriptionTier.SNA,
    ],
  },
  [AccessResource.OnDemandTrainingPage]: (options) => ({
    allowedOrgs: options?.useEdgeHomeForSNFlag ? 'all' : [MemberOrgCategory.Edge],
    // On-demand training is for every tier except Edge and Association
    allowedEdgeTiers: EVERY_TIER_BUT_EDGE_AND_ASSOCIATION,
    allowedSNTiers: options?.useEdgeHomeForSNFlag ? EVERY_TIER_BUT_EDGE_AND_ASSOCIATION : [],
  }),
  [AccessResource.AccountPage]: (options) => ({
    allowedOrgs: 'all',
    allowedEdgeTiers: 'all',
    // SN AM users always have access to the account page, every tier once on Edge home
    allowedSNTiers: options?.useEdgeHomeForSNFlag ? 'all' : [SubscriptionTier.AM],
  }),
  [AccessResource.MembershipsPage]: {
    allowedOrgs: 'all',
    // Memberships (the upgrade page) is only for Roundtable and below
    allowedEdgeTiers: ROUNDTABLE_AND_BELOW_TIERS,
    allowedSNTiers: ROUNDTABLE_AND_BELOW_TIERS,
  },
  [AccessResource.AccountSettingsPage]: (options) => ({
    allowedOrgs: options?.snSettingsLinkFlag ? 'all' : [MemberOrgCategory.Edge],
    allowedEdgeTiers: 'all',
    // SN users get a Settings link to their SN Settings page once SNSettingsLink is on
    allowedSNTiers: options?.snSettingsLinkFlag ? 'all' : [],
  }),
  [AccessResource.FAQPage]: {
    allowedOrgs: [MemberOrgCategory.Edge],
    allowedEdgeTiers: 'all',
    // FAQ page is only for Edge members
    allowedSNTiers: [],
  },
  [AccessResource.ManagePayoutMethods]: {
    allowedOrgs: 'all',
    allowedEdgeTiers: 'all',
    allowedSNTiers: 'all',
  },
  [AccessResource.HistoricalRebate]: {
    allowedOrgs: [MemberOrgCategory.ServiceNation],
    allowedEdgeTiers: [],
    allowedSNTiers: 'all',
  },
  [AccessResource.ReportPurchases]: {
    allowedOrgs: 'all',
    allowedEdgeTiers: [SubscriptionTier.SRT],
    allowedSNTiers: 'all',
  },
  // Open to every tier: the Rebate Matrix chart on "SN Logic on the Edge platform"
  // gives Edge and Association rows of their own, both pointing at Roundtable with
  // an "Upgrade" button, so hiding the matrix from them left that row unreachable
  // (SN-996). What differs per tier is the CTA, which
  // `PartnerRebateMatrixSection` decides, not whether the matrix renders at all.
  [AccessResource.PartnerRebateMatrix]: {
    allowedOrgs: 'all',
    allowedEdgeTiers: 'all',
    allowedSNTiers: 'all',
  },
  [AccessResource.Logout]: (options) => ({
    allowedOrgs: 'all',
    allowedEdgeTiers: 'all',
    // SN AM users always have access to the logout feature, every tier once on Edge home
    allowedSNTiers: options?.useEdgeHomeForSNFlag ? 'all' : [SubscriptionTier.AM],
  }),
};

/**
 * Central subscription/tier/org access-control gate for pages, nav links and
 * in-page features. Reads member info from the store itself, so callers only
 * pass *what* they're checking access to, not the member data to check it against.
 *
 * Returns a plain boolean - callers decide what to do on `false` (hide, redirect,
 * show an upgrade prompt, etc). Does not know about Optimizely, and does not
 * handle loading - see the module doc comment above.
 */
export function useHasAccess(
  resource: AccessResource,
  options?: useHasAccessOptions,
): boolean {
  const {
    company,
    isMasterAccount,
    historicRebateAccess,
    myPurchasesAccess,
  } = useAppSelector(selectMemberInformation);
  const { solutionOrgName } = useSolutionOrg();

  const isServiceNation = solutionOrgName === ServiceNationSolutionOrgName;
  let subscriptionTier = company?.subscription?.subscriptionTier;
  if (!subscriptionTier && !isServiceNation) {
    subscriptionTier = SubscriptionTier.EDGE;
  }

  const ruleOrResolver = ACCESS_RULES[resource];
  const rule = typeof ruleOrResolver === 'function' ?
    ruleOrResolver(options) :
    ruleOrResolver;
  const org = isServiceNation ? MemberOrgCategory.ServiceNation : MemberOrgCategory.Edge;

  if (rule.allowedOrgs !== 'all' && !rule.allowedOrgs.includes(org)) {
    return false;
  }

  const allowedTiers = isServiceNation ? rule.allowedSNTiers : rule.allowedEdgeTiers;
  if (allowedTiers !== 'all') {
    if (!subscriptionTier || !allowedTiers.includes(subscriptionTier)) {
      return false;
    }
  }

  // Wallet page is special for SN users: regardless of subscription tier
  // (including free/EDGE), it is hidden for SN members without SN's
  // historic_rebate_access grant.
  if (resource === AccessResource.WalletPage) {
    if (isServiceNation) {
      return !!historicRebateAccess;
    }
  }

  // Payout methods are special for SN users
  // SN sub accounts cannot edit payout methods
  if (resource === AccessResource.ManagePayoutMethods) {
    if (isServiceNation) {
      return !!isMasterAccount;
    }
  }

  // Historical rebate is only available for SN accounts with the historical rebate access
  if (resource === AccessResource.HistoricalRebate) {
    return isServiceNation && !!historicRebateAccess;
  }

  // Report My Purchases is special for SN users: every SN account, including
  // master accounts, needs an explicit my_purchases_access grant from SN -
  // there is no implicit access for master accounts.
  if (resource === AccessResource.ReportPurchases) {
    if (isServiceNation) {
      return !!myPurchasesAccess;
    }
  }

  return true;
}
